import { Switch, Tooltip } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useState } from 'react'
import type { IdentityVerificationData } from '@/hooks/useIdentityVerification'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

type PersonaEnrollment = IdentityVerificationData['personaEnrollment']

/**
 * Lets care coordinators and doctors enroll the patient in the Persona pilot, or remove them —
 * the same cohort entry the Super **Persona pilot** page writes (docs/PERSONA_IDV_PLAN.md §8). Other
 * staff see the switch read-only. The middleware applies the adult-only rule and says why it refuses.
 */
export function PersonaEnrollmentSwitch({
  patientId,
  enrollment,
  onChanged,
}: {
  patientId: string
  enrollment: PersonaEnrollment
  onChanged: () => Promise<unknown>
}) {
  const client = useClient()
  const [saving, setSaving] = useState(false)

  const setEnrolled = async (enrolled: boolean) => {
    setSaving(true)
    try {
      await client.setPersonaEnrollment({ id: patientId, enrolled })
      await onChanged()
      showNotification({
        color: 'green',
        message: enrolled
          ? 'The patient can now verify their identity online from the portal.'
          : 'Online verification turned off for this patient.',
      })
    } catch (error) {
      logError('patient', 'Set Persona pilot enrollment', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to change online verification.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Tooltip
      label={
        enrollment.canChange
          ? 'Offers Verify now in the patient portal'
          : 'Only care coordinators and doctors can change this'
      }
      withArrow
    >
      {/* The wrapper keeps the tooltip working while the switch is disabled. */}
      <div>
        <Switch
          label="Enable in patient portal"
          description="Available to the patient when enabled."
          checked={enrollment.enrolled}
          disabled={!enrollment.canChange || saving}
          onChange={(event) => void setEnrolled(event.currentTarget.checked)}
        />
      </div>
    </Tooltip>
  )
}
